import { createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  getUserInjuries,
  getInjuryById,
  addInjury as addInjuryService,
  updateInjury as updateInjuryService,
  deleteInjury as deleteInjuryService,
  buildInjuredIntervals,
  Injury,
  InjuryInput,
  InjuredInterval
} from '../../services/firebase/injury.service';

// Re-export types from service
export type { Injury, InjuryInput, InjuredInterval } from '../../services/firebase/injury.service';

interface InjuryState {
  injuries: Record<string, Injury>;
  currentInjury: Injury | null;
  error: string | null;
  injuredIntervals: InjuredInterval[];
}

// Initial state
const initialState: InjuryState = {
  injuries: {},
  currentInjury: null,
  error: null,
  injuredIntervals: []
};

// Async thunks

// Fetch all injuries for a user
export const fetchUserInjuries = createAsyncThunk(
  'injury/fetchUserInjuries',
  async (userId: string, { rejectWithValue }) => {
    try {
      const injuries = await getUserInjuries(userId);
      return injuries;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch injuries');
    }
  }
);

// Fetch a single injury by ID
export const fetchInjuryById = createAsyncThunk(
  'injury/fetchInjuryById',
  async ({ userId, injuryId }: { userId: string; injuryId: string }, { rejectWithValue }) => {
    try {
      const injury = await getInjuryById(userId, injuryId);
      if (!injury) {
        return rejectWithValue('Injury not found');
      }
      return injury;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch injury');
    }
  }
);

// Add a new injury
export const addInjury = createAsyncThunk(
  'injury/addInjury',
  async (injuryData: InjuryInput, { rejectWithValue }) => {
    try {
      const injury = await addInjuryService(injuryData);
      return injury;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add injury');
    }
  }
);

// Update an existing injury
export const updateInjury = createAsyncThunk(
  'injury/updateInjury',
  async ({ userId, id, data }: { userId: string; id: string; data: Partial<InjuryInput> }, { rejectWithValue }) => {
    try {
      const injury = await updateInjuryService(userId, id, data);
      return injury;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update injury');
    }
  }
);

// Delete an injury
export const deleteInjury = createAsyncThunk(
  'injury/deleteInjury',
  async ({ userId, injuryId }: { userId: string; injuryId: string }, { rejectWithValue }) => {
    try {
      await deleteInjuryService(userId, injuryId);
      return injuryId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete injury');
    }
  }
);

// Mark an injury as recovered
export const markRecovered = createAsyncThunk(
  'injury/markRecovered',
  async ({ userId, id, recoveryDate }: { userId: string; id: string; recoveryDate: number }, { rejectWithValue }) => {
    try {
      const injury = await updateInjuryService(userId, id, { 
        dateEnded: recoveryDate,
        isRecovered: true
      });
      return injury;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark injury as recovered');
    }
  }
);

// Slice
const injurySlice = createSlice({
  name: 'injury',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user injuries
      .addCase(fetchUserInjuries.fulfilled, (state, action) => {
        // Convert array to object indexed by ID
        const injuriesMap = action.payload.reduce((acc, injury) => {
          acc[injury.id] = injury;
          return acc;
        }, {} as Record<string, Injury>);
        
        state.injuries = injuriesMap;
        state.injuredIntervals = buildInjuredIntervals(injuriesMap);
        state.error = null;
      })
      .addCase(fetchUserInjuries.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to fetch injuries';
      })
      
      // Fetch injury by ID
      .addCase(fetchInjuryById.fulfilled, (state, action) => {
        state.currentInjury = action.payload;
        state.injuries[action.payload.id] = action.payload;
        state.injuredIntervals = buildInjuredIntervals(state.injuries);
        state.error = null;
      })
      .addCase(fetchInjuryById.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to fetch injury';
      })
      
      // Add injury
      .addCase(addInjury.fulfilled, (state, action) => {
        state.injuries[action.payload.id] = action.payload;
        state.injuredIntervals = buildInjuredIntervals(state.injuries);
        state.error = null;
      })
      .addCase(addInjury.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to add injury';
      })
      
      // Update injury
      .addCase(updateInjury.fulfilled, (state, action) => {
        state.injuries[action.payload.id] = action.payload;
        if (state.currentInjury?.id === action.payload.id) {
          state.currentInjury = action.payload;
        }
        state.injuredIntervals = buildInjuredIntervals(state.injuries);
        state.error = null;
      })
      .addCase(updateInjury.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to update injury';
      })
      
      // Delete injury
      .addCase(deleteInjury.fulfilled, (state, action) => {
        delete state.injuries[action.payload];
        if (state.currentInjury?.id === action.payload) {
          state.currentInjury = null;
        }
        state.injuredIntervals = buildInjuredIntervals(state.injuries);
        state.error = null;
      })
      .addCase(deleteInjury.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to delete injury';
      })
      
      // Mark recovered
      .addCase(markRecovered.fulfilled, (state, action) => {
        state.injuries[action.payload.id] = action.payload;
        if (state.currentInjury?.id === action.payload.id) {
          state.currentInjury = action.payload;
        }
        state.injuredIntervals = buildInjuredIntervals(state.injuries);
        state.error = null;
      })
      .addCase(markRecovered.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to mark injury as recovered';
      });
  }
});

export const { clearError } = injurySlice.actions;
export default injurySlice.reducer;

// Selectors
export const selectInjuries = (state: RootState) => state.injury.injuries;
export const selectInjuredIntervals = (state: RootState) => state.injury.injuredIntervals;
